import { useId } from 'react';
import { Languages } from 'lucide-react';
import { SegmentedControl } from '@/shared/components';
import { cn } from '@/shared/lib/cn';
import { RULES_LANGUAGE, RULES_MAX, type RulesDraft, type RulesLang } from './houseRulesModel';

interface Props {
  draft: RulesDraft;
  saved: RulesDraft;
  lang: RulesLang;
  editable: boolean;
  onLangChange: (lang: RulesLang) => void;
  onChange: (lang: RulesLang, value: string) => void;
}

/** Die Hausordnung einer Sprache — die andere ist einen Klick entfernt und wird nicht vergessen. */
export function HouseRulesEditor({ draft, saved, lang, editable, ...props }: Props) {
  const id = useId();
  const value = draft[lang];
  const unsaved = value.trim() !== saved[lang].trim();
  const other: RulesLang = lang === 'De' ? 'En' : 'De';
  const length = value.trim().length;

  return (
    <section
      aria-labelledby={`${id}-title`}
      className="border-line/50 bg-card shadow-card rounded-lg border"
    >
      <div className="border-line/60 flex flex-wrap items-center justify-between gap-3 border-b px-5 py-3">
        <h3 id={`${id}-title`} className="text-ink font-serif text-lg">
          Text
        </h3>
        <SegmentedControl
          size="touch"
          aria-label="Sprache"
          shape="square"
          value={lang}
          onChange={props.onLangChange}
          options={(['De', 'En'] as const).map((item) => ({
            value: item,
            label: `${RULES_LANGUAGE[item]}${draft[item].trim() ? '' : ' (leer)'}`,
          }))}
        />
      </div>
      <div className="space-y-2 px-5 py-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <label htmlFor={id} className="text-ink text-sm font-semibold">
            Hausordnung ({RULES_LANGUAGE[lang]})
          </label>
          <span className="text-ink-soft inline-flex items-center gap-1.5 text-xs">
            <span
              aria-hidden
              className={cn(
                'size-2 rounded-full',
                unsaved
                  ? 'bg-amber'
                  : saved[lang].trim()
                    ? 'bg-sage'
                    : 'ring-ink-soft ring-[1.5px] ring-inset',
              )}
            />
            {unsaved ? 'Entwurf · ungespeichert' : saved[lang].trim() ? 'Gespeichert' : 'Noch leer'}
          </span>
        </div>
        <textarea
          id={id}
          value={value}
          readOnly={!editable}
          rows={10}
          maxLength={RULES_MAX}
          aria-describedby={`${id}-hint`}
          placeholder={
            lang === 'De'
              ? 'Nachtruhe von 22 bis 7 Uhr – bitte auch im Treppenhaus.'
              : 'Quiet hours from 10 pm to 7 am – in the stairwell too.'
          }
          onChange={(event) => props.onChange(lang, event.target.value)}
          className="border-line text-ink placeholder:text-ink-faint focus:border-sage focus:ring-sage/20 block min-h-56 w-full resize-y rounded-md border bg-white px-3 py-2 text-sm leading-relaxed outline-none read-only:bg-transparent focus:ring-2"
        />
        <div className="text-ink-soft flex flex-wrap justify-between gap-x-3 gap-y-1 text-xs">
          <span id={`${id}-hint`}>Eine Regel pro Zeile liest sich am Handy am besten.</span>
          <span className="tabular-nums">
            {length.toLocaleString('de-DE')} / {RULES_MAX.toLocaleString('de-DE')}
          </span>
        </div>
      </div>
      {!draft[other].trim() && (
        <p className="border-line/60 bg-card-alt/50 text-ink flex gap-2 border-t px-5 py-3 text-xs">
          <Languages size={14} className="mt-px shrink-0" aria-hidden />
          <span>
            <b>{RULES_LANGUAGE[other]} ist noch leer.</b>{' '}
            {value.trim()
              ? `Gäste mit dieser Sprache sehen bis dahin die ${lang === 'De' ? 'deutsche' : 'englische'} Fassung.`
              : 'Ohne Text in einer der beiden Sprachen zeigt das Portal keine Hausordnung.'}
          </span>
        </p>
      )}
    </section>
  );
}
